/**
 * Distributions as buckets (D531): the percentile of a bucketed distribution, and the key of a pickup cell. Pure.
 *
 * WHY BUCKETS. A percentile is not additive: it cannot be summed per hour and read per day, and an exact one needs every record. The catalog therefore
 * answers "how long does a match take" as a COUNT per bucket (`match_time_bucket`, `pickup_time_bucket`, `eta_deviation_bucket`: a plain `COUNT` that
 * IS additive and goes through every filter, comparison and market scope) and a screen reads the percentile off the buckets with
 * {@link percentileFromBuckets}. The result is an APPROXIMATION - it says which bucket the percentile falls in and interpolates linearly inside it -
 * and every screen that prints one says so ("about"). An open end (below the first edge, above the last) is bounded by `lowest` / `highest`, so a
 * percentile that lands there is clamped, never invented.
 */
import { SEMANTIC_VOCABULARY } from './catalog.js';
/** The ranges of the buckets a `bucket` key cuts, in order. */
export function bucketRanges(edges) {
    const out = [];
    for (let i = 0; i <= edges.length; i += 1) {
        out.push({ index: i, key: String(i).padStart(2, '0'), from: i === 0 ? null : edges[i - 1], to: i === edges.length ? null : edges[i] });
    }
    return out;
}
/** The named distributions of the catalog, so a screen and the catalog agree on the edges by construction. */
export const BUCKET_EDGES = {
    match_time_bucket: SEMANTIC_VOCABULARY.buckets.matchSeconds,
    pickup_time_bucket: SEMANTIC_VOCABULARY.buckets.pickupSeconds,
    eta_deviation_bucket: SEMANTIC_VOCABULARY.buckets.etaDeviationSeconds,
    shift: SEMANTIC_VOCABULARY.buckets.shiftHours,
    // D533: the stages of a food order
    prep_time_bucket: SEMANTIC_VOCABULARY.buckets.prepSeconds,
    pickup_wait_bucket: SEMANTIC_VOCABULARY.buckets.pickupWaitSeconds,
    delivery_time_bucket: SEMANTIC_VOCABULARY.buckets.transitSeconds,
    total_time_bucket: SEMANTIC_VOCABULARY.buckets.totalSeconds,
};
/**
 * The `p`-quantile (0 < p <= 1) of a bucketed distribution: find the bucket where the cumulative share first reaches `p` and interpolate linearly inside
 * it. `undefined` when there is nothing to measure (no counts).
 */
export function percentileFromBuckets(input, p) {
    if (!(p > 0 && p <= 1))
        throw new RangeError('p must be in (0, 1]');
    const ranges = bucketRanges(input.edges);
    const counts = ranges.map((r) => Math.max(0, input.counts[r.key] ?? 0));
    const total = counts.reduce((a, b) => a + b, 0);
    if (total === 0)
        return undefined;
    const target = p * total;
    let seen = 0;
    for (const r of ranges) {
        const n = counts[r.index];
        if (n === 0)
            continue;
        if (seen + n >= target) {
            const lowest = input.lowest ?? 0;
            const lastEdge = input.edges[input.edges.length - 1];
            const prevEdge = input.edges[input.edges.length - 2] ?? lowest;
            const from = r.from ?? Math.min(lowest, input.edges[0]);
            const to = r.to ?? input.highest ?? lastEdge + (lastEdge - prevEdge);
            const open = r.from === null || r.to === null;
            const value = from + ((target - seen) / n) * (to - from);
            // An open end has no honest width: a percentile that lands in it is reported at its bound.
            const clamped = open && ((r.from === null && value <= from) || (r.to === null && input.highest !== undefined && value >= to));
            return { value: Math.round(value * 10) / 10, exact: false, bucket: r.key, clamped };
        }
        seen += n;
    }
    return undefined;
}
/** The cumulative share (0..1) at the END of every bucket, for the curve under a histogram. Empty distribution: all zeros. */
export function cumulativeShares(counts, edges) {
    const ranges = bucketRanges(edges);
    const values = ranges.map((r) => Math.max(0, counts[r.key] ?? 0));
    const total = values.reduce((a, b) => a + b, 0);
    let seen = 0;
    return values.map((n) => {
        seen += n;
        return total === 0 ? 0 : seen / total;
    });
}
// ───────────────────────────── the pickup cell ─────────────────────────────
const METERS_PER_DEGREE = 111_320;
/**
 * The bounds of a cell, by the scheme the ETL writes (`@vello/analytics-etl` `pickupCellOf`, pinned to this by a test there):
 * `latStep = meters / 111320`, `lngStep = meters / (111320 x cos(refLat))`, `row = floor((lat + 90) / latStep)`, `col = floor((lng + 180) / lngStep)`.
 * Null for a key that is not `row:col:meters:refLatMdeg` (a malformed key is dropped by the map, never drawn at 0,0).
 */
export function pickupCellBounds(key) {
    const m = /^(\d{1,9}):(\d{1,9}):(\d{3,5}):(-?\d{1,6})$/.exec(key);
    if (m === null)
        return null;
    const row = Number(m[1]);
    const col = Number(m[2]);
    const meters = Number(m[3]);
    const refLat = Number(m[4]) / 1000;
    if (meters < SEMANTIC_VOCABULARY.geoCell.minMeters || meters > SEMANTIC_VOCABULARY.geoCell.maxMeters || Math.abs(refLat) > 90)
        return null;
    const latStep = meters / METERS_PER_DEGREE;
    const lngStep = meters / (METERS_PER_DEGREE * Math.max(0.01, Math.cos((refLat * Math.PI) / 180)));
    const south = row * latStep - 90;
    const west = col * lngStep - 180;
    return { key, south, north: south + latStep, west, east: west + lngStep, centerLat: south + latStep / 2, centerLng: west + lngStep / 2, meters };
}
//# sourceMappingURL=buckets.js.map